import { computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { PrivaLocalizationService } from '@priva/localization';
import { withId } from '@priva/utilities/predicate';

import { AppSettings } from 'app/features/settings/models';
import { selectAppSettings } from 'app/features/settings/state/settings/settings.selectors';
import { selectActivityList } from 'app/state/activities/activity.selectors';

import { Tile, tileGroupName } from '../../components/tile-list/models';
import { Activity } from '../../features/activity-selector/models';
import { getSortedActivitiesForManualInputFlow } from '../../features/activity-selector/utils';
import { endBreakActivityToTimeRegistrationDefinition } from './models/time-registration-definition';
import {
    createLoginTile,
    createLogoutTile,
    createStartBreakTile,
    mapTimeRegistrationDefinitionToTile,
} from './utils/system-and-break-activity.util';

type SystemAndBreakActivityState = {
    timeActivities: Tile[];
    breakActivities: Tile[];
};
const initialState: SystemAndBreakActivityState = {
    timeActivities: [],
    breakActivities: [],
};

export const SystemAndBreakActivityStore = signalStore(
    withState(initialState),
    withComputed(() => {
        // TODO[SignalStore]: convert this to use signal store selectors once implemented
        const ngrxStore = inject(Store);
        const appMode$ = ngrxStore.select(selectAppSettings).pipe(
            filter((settings): settings is AppSettings => !!settings),
            map((settings) => settings.appMode),
        );
        const endBreakActivities$ = ngrxStore.select(selectActivityList).pipe(
            filter((list): list is Activity[] => !!list),
            map((activityList) => getSortedActivitiesForManualInputFlow('time-registration', activityList)),
            map((endBreakActivities) =>
                endBreakActivities.map((activity) => endBreakActivityToTimeRegistrationDefinition(activity)),
            ),
        );
        return {
            appMode: toSignal(appMode$),
            _endBreakActivities: toSignal(endBreakActivities$, { initialValue: [] }),
        };
    }),
    withComputed((state) => ({
        filteredTiles: computed(() => [...state.timeActivities(), ...state.breakActivities()]),
    })),
    withMethods((state) => {
        const privaLocalizationService = inject(PrivaLocalizationService);
        const startBreakTile = createStartBreakTile(privaLocalizationService);
        const loginTile = createLoginTile(privaLocalizationService);
        const logoutTile = createLogoutTile(privaLocalizationService);

        const defaultTimeActivities = [loginTile, logoutTile, startBreakTile];
        const endBreakTitleGroupName =
            state.appMode() === 'multi-user' ? tileGroupName.EndBreak : tileGroupName.Break;
        return {
            setTimeActivities: () => {
                patchState(state, {
                    timeActivities: state.appMode() === 'multi-user' ? defaultTimeActivities : [],
                });
            },
            setBreakActivities: () => {
                const endBreakRegistrationTiles = state
                    ._endBreakActivities()
                    .map((d) => mapTimeRegistrationDefinitionToTile(d.name, d, '', endBreakTitleGroupName));
                patchState(state, {
                    breakActivities: [...endBreakRegistrationTiles],
                });
            },
            getEndBreakActivity: (tileId: string) => {
                return state._endBreakActivities().find(withId(tileId))?.activity;
            },
        };
    }),
    withHooks((store) => ({
        onInit() {
            effect(() => {
                store.setBreakActivities();
            });
            effect(() => {
                store.setTimeActivities();
            });
        },
    })),
);
